import React from "react";
import { Alert, Button, Checkbox, Input, Space, Card, Row, Col, Form, Select } from "antd";
import {
  SearchOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  UserOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import CardPage from "../../layout/CardPage";
import { SelectorCliente } from "./features/SelectorCliente/SelectorCliente";
import { AutorizacionSupervisor } from "./features/AutorizacionSupervisor/AutorizacionSupervisor";
import { ServiciosRelacionados } from "./features/ServiciosRelacionados/ServiciosRelacionados";
import { PolizarioAguila } from "./features/Polizarios/PolizarioAguila";
import { PolizarioChubb } from "./features/Polizarios/PolizarioChubb";
import { PolizarioGS } from "./features/Polizarios/PolizarioGS";
import { PolizarioEdega } from "./features/Polizarios/PolizarioEdega";
import { InformacionCampaniaDrawer } from "./features/InformacionCampania/InformacionCampaniaDrawer";
import { CLIENTES_IDS } from "../../constants/cons";
import { AyudaContextual } from "../../components/avisos/AyudaContextual";
import { useCrearServiciosV2 } from "./hooks/useCrearServiciosV2";
import "./shared/crearServiciosV2.css";

const { Option } = Select;

const getRequiredFields = (clienteSeleccionado) => {
  const baseFields = ["nombre", "apellidoPaterno", "apellidoMaterno", "telefono", "tipoAsistenciaID", "servicioID"];
  if (clienteSeleccionado === CLIENTES_IDS.GS) {
    return [...baseFields, "sucursal", "ramo", "poliza", "inciso"];
  }
  return [...baseFields, "validador1"];
};

const formNoListo = (form, clienteSeleccionado) => {
  const requiredFields = getRequiredFields(clienteSeleccionado);
  const values = form.getFieldsValue(requiredFields);
  const camposVacios = requiredFields.some((f) => !values[f]);
  const telefonoInvalido = values.telefono && values.telefono.length !== 10;
  const tieneErrores = form.getFieldsError().filter(({ errors }) => errors.length).length > 0;
  return camposVacios || telefonoInvalido || tieneErrores;
};

export const CrearServiciosV2 = () => {
  const [form] = Form.useForm();
  const telefonoEsWA = Form.useWatch("telefonoEsWA", form);
  const servicioID = Form.useWatch("servicioID", form);

  const {
    clientesCampanias,
    campaniaSeleccionada,
    clienteSeleccionado,
    claveSupervisor,
    serviciosRelacionados,
    serviciosRegistrados,
    requiereAutorizacion,
    polizarioBloqueado,
    mostrarFormularioValidador,
    mostrarResultados,
    loadingBusqueda,
    loadingAutorizacion,
    servicios,
    opcionesTipos,
    opcionesServicios,
    handleClienteChange,
    handleCampaniaChange,
    handleBuscarRelacionados,
    handleAutorizar,
    handleGenerarOP,
    handleTipoAsistenciaChange,
    handleFormValuesChange,
    setClaveSupervisor,
  } = useCrearServiciosV2(form);

  return (
    <CardPage titulo="Crear Servicio V2">
      <div className="crear-servicios-v2">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card size="small">
                <div className="crear-servicios-v2__section-header">
                  <div className="crear-servicios-v2__section-header-title">
                    <AppstoreOutlined />
                    <span>Seleccione Cliente y Campaña</span>
                  </div>
                </div>
                <SelectorCliente
                  clientesCampanias={clientesCampanias}
                  clienteSeleccionado={clienteSeleccionado}
                  campaniaSeleccionada={campaniaSeleccionada}
                  onClienteChange={handleClienteChange}
                  onCampaniaChange={handleCampaniaChange}
                />
              </Card>

              {mostrarFormularioValidador && (
                <Card size="small">
                  <div className="crear-servicios-v2__section-header">
                    <div className="crear-servicios-v2__section-header-title">
                      <SearchOutlined />
                      <span>Buscar Validador</span>
                    </div>
                  </div>
                  <Form
                    form={form}
                    layout="vertical"
                    size="large"
                    onValuesChange={handleFormValuesChange}
                  >
                    <Row gutter={12}>
                      <Col xs={24} sm={6}>
                        <Form.Item
                          label="Nombre"
                          name="nombre"
                          rules={[{ required: true, message: "Nombre necesario" }]}
                        >
                          <Input
                            prefix={<UserOutlined />}
                            placeholder="Nombre"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={6}>
                        <Form.Item
                          label="Apellido Paterno"
                          name="apellidoPaterno"
                          rules={[{ required: true, message: "Apellido paterno necesario" }]}
                        >
                          <Input
                            placeholder="Apellido Paterno"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={6}>
                        <Form.Item
                          label="Apellido Materno"
                          name="apellidoMaterno"
                          rules={[{ required: true, message: "Apellido materno necesario" }]}
                        >
                          <Input
                            placeholder="Apellido Materno"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={6}>
                        <Form.Item
                          label={(
                            <Space size={6}>
                              <span>Teléfono (10 dígitos)</span>
                              <Form.Item
                                name="telefonoEsWA"
                                valuePropName="checked"
                                noStyle
                              >
                                <Checkbox style={{ fontSize: 12 }}>WhatsApp</Checkbox>
                              </Form.Item>
                            </Space>
                          )}
                          name="telefono"
                          rules={[
                            { required: true, message: "Teléfono necesario" },
                            { len: 10, message: "Teléfono debe tener 10 dígitos" },
                          ]}
                        >
                          <Input
                            prefix={<PhoneOutlined />}
                            placeholder="10 dígitos"
                            maxLength={10}
                          />
                        </Form.Item>
                      </Col>
                      {telefonoEsWA && (
                        <Col xs={24}>
                          <Alert
                            type="info"
                            showIcon
                            message='Si el usuario acepta notificaciones por WhatsApp, indícale que recibirá un mensaje y que deberá seleccionar la opción "Recibir Notificaciones".'
                          />
                        </Col>
                      )}
                    </Row>

                    {clienteSeleccionado === CLIENTES_IDS.GS ? (
                      <>
<Row gutter={[12, 0]} align="bottom">
                          <Col xs={24} sm={6} style={{ display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                            <div style={{ flex: 1 }}>
                              <Form.Item
                                label="Sucursal"
                                name="sucursal"
                                rules={[{ required: true, message: "Sucursal requerida" }]}
                              >
                                <Input
                                  type="number"
                                  placeholder="7"
                                  min="0"
                                  style={{ fontSize: '13px', padding: '6px 6px' }}
                                />
                              </Form.Item>
                            </div>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#999', paddingBottom: '32px' }}>/</span>
                          </Col>
                          <Col xs={24} sm={6} style={{ display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                            <div style={{ flex: 1 }}>
                              <Form.Item
                                label="Ramo"
                                name="ramo"
                                rules={[{ required: true, message: "Ramo requerido" }]}
                              >
                                <Input
                                  type="number"
                                  placeholder="822"
                                  min="0"
                                  style={{ fontSize: '13px', padding: '6px 6px' }}
                                />
                              </Form.Item>
                            </div>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#999', paddingBottom: '32px' }}>/</span>
                          </Col>
                          <Col xs={24} sm={6} style={{ display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                            <div style={{ flex: 1 }}>
                              <Form.Item
                                label="Póliza"
                                name="poliza"
                                rules={[{ required: true, message: "Póliza requerida" }]}
                              >
                                <Input
                                  type="number"
                                  placeholder="482709"
                                  min="0"
                                  style={{ fontSize: '13px', padding: '6px 6px' }}
                                />
                              </Form.Item>
                            </div>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#999', paddingBottom: '32px' }}>/</span>
                          </Col>
                          <Col xs={24} sm={6}>
                            <Form.Item
                              label="Inciso"
                              name="inciso"
                              rules={[{ required: true, message: "Inciso requerido" }]}
                            >
                              <Input
                                type="number"
                                placeholder="1"
                                min="0"
                                style={{ fontSize: '13px', padding: '6px 6px' }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[12, 0]}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label="Tipo de Asistencia"
                              name="tipoAsistenciaID"
                              rules={[{ required: true, message: "Tipo de asistencia requerido" }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Tipo"
                                onChange={handleTipoAsistenciaChange}
                                style={{ width: "100%" }}
                              >
                                {opcionesTipos.map((tipo) => (
                                  <Option key={tipo.valor} value={tipo.valor}>
                                    {tipo.texto}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label={(
                                <Space size={6}>
                                  <span>Servicio</span>
                                  {Number(campaniaSeleccionada) === 59 && Number(servicioID) === 1 && (
                                    <AyudaContextual>
                                      <p>Identifica el tipo de vehículo y selecciona la opción correcta:</p>
                                      <ul className="ayuda-contextual__lista">
                                        <li>Arrastre de grúa - Vehículo ligero</li>
                                        <li>Grúa equipo pesado - Vehículo pesado</li>
                                      </ul>
                                      <p className="ayuda-contextual__texto">Revisa que los datos de la póliza coincidan con los que te brindan en llamada y que se encuentre vigente.</p>
                                    </AyudaContextual>
                                  )}
                                  {Number(campaniaSeleccionada) === 59 && Number(servicioID) === 7 && (
                                    <AyudaContextual>
                                      Distingue si el ajustador requiere el servicio en el sitio o si fue trasladado por un proveedor concesionado (Folio 300).
                                    </AyudaContextual>
                                  )}
                                </Space>
                              )}
                              name="servicioID"
                              rules={[
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (!getFieldValue("tipoAsistenciaID")) return Promise.resolve();
                                    if (!value) return Promise.reject(new Error("Servicio requerido"));
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Servicio"
                                style={{ width: "100%" }}
                                disabled={!form.getFieldValue("tipoAsistenciaID")}
                              >
                                {opcionesServicios.map((servicio) => (
                                  <Option key={servicio.valor} value={servicio.valor}>
                                    {servicio.texto}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <Row gutter={12}>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            label="Validador 1"
                            name="validador1"
                            rules={[
                              { required: true, message: "Validador necesario" },
                            ]}
                          >
                            <Input
                              prefix={<SafetyCertificateOutlined />}
                              placeholder="Validador 1"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            label="Tipo de Asistencia"
                            name="tipoAsistenciaID"
                            rules={[{ required: true, message: "Tipo de asistencia requerido" }]}
                          >
                            <Select
                              placeholder="Tipo de Asistencia"
                              onChange={handleTipoAsistenciaChange}
                              style={{ width: "100%" }}
                            >
                              {opcionesTipos.map((tipo) => (
                                <Option key={tipo.valor} value={tipo.valor}>
                                  {tipo.texto}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            label="Servicio"
                            name="servicioID"
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!getFieldValue("tipoAsistenciaID")) return Promise.resolve();
                                  if (!value) return Promise.reject(new Error("Servicio requerido"));
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Select
                              placeholder="Servicio"
                              style={{ width: "100%" }}
                              disabled={!form.getFieldValue("tipoAsistenciaID")}
                            >
                              {opcionesServicios.map((servicio) => (
                                <Option key={servicio.valor} value={servicio.valor}>
                                  {servicio.texto}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    <Row gutter={12}>
                      <Col xs={24}>
                        <Form.Item
                          label={(
                            <Space size={6}>
                              <span>¿Hay Ajustador? Nombre:</span>
                              {Number(campaniaSeleccionada) === 59 && (
                                <AyudaContextual>
                                  Distingue si el ajustador requiere el servicio en el sitio o si fue trasladado por un proveedor concesionado (Folio 300).
                                </AyudaContextual>
                              )}
                            </Space>
                          )}
                          name="nombreAjustador"
                        >
                          <Input
                            prefix={<ToolOutlined />}
                            placeholder="Nombre del Ajustador (opcional)"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item noStyle>
                          <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            block
                            size="large"
                            loading={loadingBusqueda}
                            disabled={formNoListo(form, clienteSeleccionado)}
                            onClick={() => form.validateFields().then(handleBuscarRelacionados)}
                          >
                            Buscar
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>
              )}

              {mostrarResultados && !loadingBusqueda && (
                <Card size="small">
                  <div className="crear-servicios-v2__section-header">
                    <div className="crear-servicios-v2__section-header-title">
                      <UnorderedListOutlined />
                      <span>Servicios Relacionados</span>
                    </div>
                  </div>

                  <AutorizacionSupervisor
                    form={form}
                    claveSupervisor={claveSupervisor}
                    setClaveSupervisor={setClaveSupervisor}
                    onAutorizar={handleAutorizar}
                    loading={loadingAutorizacion}
                    requiereAutorizacion={requiereAutorizacion}
                  />

                  <ServiciosRelacionados
                    campaniaID={campaniaSeleccionada}
                    form={form}
                    serviciosRelacionados={serviciosRelacionados}
                    serviciosRegistrados={serviciosRegistrados}
                    onGenerarOP={handleGenerarOP}
                    loadingGenerarOP={loadingAutorizacion}
                    formNoListo={formNoListo(form, clienteSeleccionado)}
                    polizarioBloqueado={polizarioBloqueado}
                  />
                </Card>
              )}
            </Space>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {clienteSeleccionado && campaniaSeleccionada && (
                <InformacionCampaniaDrawer campaniaID={campaniaSeleccionada} />
              )}
              {clienteSeleccionado === CLIENTES_IDS.EL_AGUILA && <PolizarioAguila />}
              {clienteSeleccionado === CLIENTES_IDS.CHUBB && <PolizarioChubb />}
              {clienteSeleccionado === CLIENTES_IDS.GS && <PolizarioGS />}
              {(clienteSeleccionado === CLIENTES_IDS.EDEGA || clienteSeleccionado === CLIENTES_IDS.EDEGA_PROD || clienteSeleccionado === CLIENTES_IDS.EDEGA_QA) && <PolizarioEdega />}
            </Space>
          </Col>
        </Row>
      </div>
    </CardPage>
  );
};
